import type { ColumnDef } from "@tanstack/react-table";
import type { SortDescriptor } from "react-stately";

import { memo, useCallback, useMemo } from "react";

import { Link } from "@/components/link";
import { StarRatingDisplay } from "@/components/star-rating";
import {
  type PaginationMetadata,
  DraggableTable as Table,
} from "@/components/table";
import { useTableSorting } from "@/hooks/useTableSorting";
import { dateFormatter } from "@/utils/dateFormatter";

type PerformanceRequestItem = {
  assignee: null | string;
  assigneeId: null | number;
  completedAt: Date | null;
  createdAt: Date | null;
  feedback: null | string;
  id: number;
  name: string;
  projectName: null | string;
  rating: null | number;
};

interface PerformanceRequestsTableProps {
  isFetching?: boolean;
  pagination?: {
    metadata: PaginationMetadata;
    onItemsPerPageChange?: (itemsPerPage: number) => void;
    onPageChange: (page: number) => void;
    pageSizeOptions?: number[];
    showPageSizeSelector?: boolean;
  };
  requests: PerformanceRequestItem[];
  setSortDescriptor: (sortDescriptor: SortDescriptor) => void;
  sortDescriptor: SortDescriptor;
}

const RequestNameCell = memo(function RequestNameCell({
  id,
  name,
}: {
  id: number;
  name: string;
}) {
  return (
    <Link className="hover:no-underline" href={`/requests/${id}/edit`}>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-text-primary leading-tight min-w-0 line-clamp-2 break-words">
          {name ?? "-"}
        </p>
      </div>
    </Link>
  );
});

const TextCell = memo(function TextCell({ value }: { value: null | string }) {
  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden">
      <span className="text-text-tertiary text-sm">{value ?? "-"}</span>
    </div>
  );
});

const DateCell = memo(function DateCell({ value }: { value: string }) {
  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden">
      <span className="text-text-tertiary text-sm">
        {value ? dateFormatter(value) : "-"}
      </span>
    </div>
  );
});

const RatingCell = memo(function RatingCell({
  value,
}: {
  value: null | number;
}) {
  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden">
      <StarRatingDisplay round="floor" value={value} />
    </div>
  );
});

export const PerformanceRequestsTable = memo(function PerformanceRequestsTable({
  isFetching = false,
  pagination,
  requests,
  setSortDescriptor,
  sortDescriptor,
}: PerformanceRequestsTableProps) {
  const { handleSort, sorting } = useTableSorting({
    defaultColumn: "createdAt",
    defaultDirection: "descending",
    setSortDescriptor,
    sortDescriptor,
  });

  const columns: ColumnDef<PerformanceRequestItem>[] = useMemo(
    () => [
      {
        accessorKey: "name",
        cell: ({ row }) => (
          <RequestNameCell id={row.original.id} name={row.original.name} />
        ),
        enableSorting: true,
        header: "Name",
        maxSize: 300,
        minSize: 300,
        size: 300,
      },
      {
        accessorKey: "projectName",
        cell: ({ row }) => <TextCell value={row.original.projectName} />,
        enableSorting: false,
        header: "Account",
        maxSize: 180,
        minSize: 180,
        size: 180,
      },
      {
        accessorKey: "completedAt",
        cell: ({ getValue }) => <DateCell value={getValue() as string} />,
        enableSorting: true,
        header: "Completed date",
        maxSize: 150,
        minSize: 150,
        size: 150,
      },
      {
        accessorKey: "rating",
        cell: ({ row }) => <RatingCell value={row.original.rating} />,
        enableSorting: true,
        header: "Rating",
        maxSize: 120,
        minSize: 120,
        size: 120,
      },
      {
        accessorKey: "createdAt",
        cell: ({ getValue }) => <DateCell value={getValue() as string} />,
        enableSorting: true,
        header: "Created on",
        maxSize: 150,
        minSize: 150,
        size: 150,
      },
    ],
    [],
  );

  const getRowId = useCallback(
    (row: PerformanceRequestItem) => String(row.id),
    [],
  );

  const paginationConfig = useMemo(
    () =>
      pagination
        ? {
            metadata: pagination.metadata,
            onItemsPerPageChange: pagination.onItemsPerPageChange,
            onPageChange: pagination.onPageChange,
            pageSizeOptions: pagination.pageSizeOptions,
            showPageSizeSelector: pagination.showPageSizeSelector,
          }
        : undefined,
    [
      pagination?.metadata,
      pagination?.onItemsPerPageChange,
      pagination?.onPageChange,
      pagination?.pageSizeOptions,
      pagination?.showPageSizeSelector,
    ],
  );

  const sortableColumns = ["name", "completedAt", "rating", "createdAt"];

  return (
    <div
      className={`rounded-b-xl bg-bg-primary overflow-auto transition-opacity duration-200 ${
        isFetching ? "opacity-50 pointer-events-none" : "opacity-100"
      }`}
    >
      <Table<PerformanceRequestItem>
        className="rounded-b-xl w-full"
        columns={columns}
        data={requests}
        getRowId={getRowId}
        onSort={handleSort}
        pagination={paginationConfig}
        sortableColumns={sortableColumns}
        sorting={sorting}
      />
    </div>
  );
});
